import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import Textarea from 'components/inputs/textarea';
import { StructureSubTypeRuleAdminType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

export default function StructureSubTypeRulesPage() {
  const dispatch = useAppDispatch();
  const { structureSubTypeRules, structureTypes } = useAppSelector(
    (state) => state.admin
  );
  const [localRules, setLocalRules] = useState<StructureSubTypeRuleAdminType[]>(
    []
  );
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTypeId, setNewTypeId] = useState<number>(0);
  const [newSubtypeId, setNewSubtypeId] = useState<number>(0);
  const [newContext, setNewContext] = useState('');

  useEffect(() => {
    setLocalRules(structureSubTypeRules);
  }, [structureSubTypeRules]);

  const filteredRules = useMemo(() => {
    if (!search) return localRules;
    const lower = search.toLowerCase();
    return localRules.filter(
      (r) =>
        r.structureTypeLabel.toLowerCase().includes(lower) ||
        r.structureSubtypeLabel.toLowerCase().includes(lower) ||
        r.context.toLowerCase().includes(lower)
    );
  }, [localRules, search]);

  const existingCombinations = useMemo(
    () =>
      new Set(
        structureSubTypeRules.map(
          (r) => `${r.structureTypeId}-${r.structureSubtypeId}`
        )
      ),
    [structureSubTypeRules]
  );

  const selectedType = structureTypes.find((t) => t.id === newTypeId);

  const availableSubtypes = useMemo(() => {
    if (!selectedType) return [];
    return selectedType.subtypes.filter(
      (s) => !existingCombinations.has(`${selectedType.id}-${s.id}`)
    );
  }, [selectedType, existingCombinations]);

  const updateContext = (id: number, context: string) => {
    setLocalRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, context } : r))
    );
  };

  const hasChanges =
    JSON.stringify(localRules) !== JSON.stringify(structureSubTypeRules);

  const handleSave = async () => {
    try {
      const changed = localRules.filter((r) => {
        const original = structureSubTypeRules.find((o) => o.id === r.id);
        return original && original.context !== r.context;
      });
      await axios.patch(
        'structure-sub-type-rules',
        changed.map((r) => ({ id: r.id, context: r.context }))
      );
      dispatch(fetchDefaultAdminData());
      successModal('Structure sub-type rules updated successfully.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    setLocalRules(structureSubTypeRules);
  };

  const handleCreate = async () => {
    if (!newTypeId || !newSubtypeId || !newContext.trim()) {
      errorModal('Structure type, subtype, and context are required.');
      return;
    }
    try {
      await axios.post('structure-sub-type-rule', {
        structureTypeId: newTypeId,
        structureSubtypeId: newSubtypeId,
        context: newContext.trim()
      });
      dispatch(fetchDefaultAdminData());
      setShowCreateForm(false);
      setNewTypeId(0);
      setNewSubtypeId(0);
      setNewContext('');
      successModal('Rule created.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleDelete = (rule: StructureSubTypeRuleAdminType) => {
    confirmationModal(
      `Delete rule for "${rule.structureTypeLabel} / ${rule.structureSubtypeLabel}"? This cannot be undone.`,
      async () => {
        try {
          await axios.delete(`structure-sub-type-rule/${rule.id}`);
          dispatch(fetchDefaultAdminData());
          successModal('Rule deleted.');
        } catch {
          errorModal('Something went wrong.');
        }
      }
    );
  };

  const handleOpenCreate = () => {
    setShowCreateForm(true);
    const firstType = structureTypes[0];
    if (firstType) {
      setNewTypeId(firstType.id);
      const firstAvailable = firstType.subtypes.find(
        (s) => !existingCombinations.has(`${firstType.id}-${s.id}`)
      );
      setNewSubtypeId(firstAvailable?.id ?? 0);
    }
  };

  useEffect(() => {
    if (!showCreateForm || !newTypeId) return;
    const firstAvailable = availableSubtypes[0];
    setNewSubtypeId(firstAvailable?.id ?? 0);
  }, [newTypeId, showCreateForm]);

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        Structure Sub-Type Rules
      </div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
        {!showCreateForm && (
          <Button
            text='Add Rule'
            color='primary'
            onClick={handleOpenCreate}
            icon={PlusIcon}
          />
        )}
        <div className='ml-auto w-64'>
          <Text
            value={search}
            onChange={setSearch}
            placeholder='Search type, subtype, or context...'
            controlled
          />
        </div>
      </div>

      {showCreateForm && (
        <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
          <div className='mb-3 text-sm font-semibold text-blue-800'>
            New Rule
          </div>
          <div className='mb-3 grid grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1'>
              <label className='text-sm font-semibold text-gray-700'>
                Structure Type
              </label>
              <Select
                value={newTypeId}
                onChange={(v) => setNewTypeId(Number(v))}
                options={structureTypes.map((t) => ({
                  label: t.label,
                  value: t.id
                }))}
                preventDeselection
              />
            </div>
            <div className='flex flex-col gap-1'>
              <label className='text-sm font-semibold text-gray-700'>
                Subtype
              </label>
              <Select
                value={newSubtypeId}
                onChange={(v) => setNewSubtypeId(Number(v))}
                options={availableSubtypes.map((s) => ({
                  label: s.label,
                  value: s.id
                }))}
                preventDeselection
              />
              {availableSubtypes.length === 0 && (
                <span className='text-xs text-gray-500'>
                  All subtypes for this type already have rules.
                </span>
              )}
            </div>
          </div>
          <div className='mb-3 flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Context
            </label>
            <Textarea
              value={newContext}
              onChange={(v) => setNewContext(v)}
              placeholder='Enter context rules for this structure sub-type...'
              rows={4}
            />
          </div>
          <div className='flex items-center gap-3'>
            <Button
              text='Create'
              color='green'
              onClick={handleCreate}
              disabled={!newTypeId || !newSubtypeId || !newContext.trim()}
            />
            <Button
              text='Cancel'
              color='white'
              onClick={() => {
                setShowCreateForm(false);
                setNewTypeId(0);
                setNewSubtypeId(0);
                setNewContext('');
              }}
            />
          </div>
        </div>
      )}

      <div className='flex flex-col gap-3'>
        {filteredRules.map((rule) => (
          <div
            key={rule.id}
            className='flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
          >
            <div className='w-72 shrink-0 pt-2'>
              <div className='flex flex-col gap-1'>
                <span className='rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700'>
                  {rule.structureTypeLabel}
                </span>
                <span className='rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700'>
                  {rule.structureSubtypeLabel}
                </span>
              </div>
            </div>
            <div className='min-w-0 flex-1'>
              <Textarea
                value={rule.context}
                onChange={(value) => updateContext(rule.id, value)}
                rows={4}
                controlled
              />
            </div>
            <div className='shrink-0 pt-2'>
              <button
                type='button'
                onClick={() => handleDelete(rule)}
                className='rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600'
                title='Delete rule'
              >
                <TrashIcon className='h-5 w-5' />
              </button>
            </div>
          </div>
        ))}
        {filteredRules.length === 0 && (
          <div className='py-8 text-center text-gray-500'>
            No rules match your search.
          </div>
        )}
      </div>
      <div className='text-sm text-gray-400'>
        {filteredRules.length} rule{filteredRules.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
